import { Divider, Form } from 'antd';
import { PageProps } from '@/types/PageProps';
import { DefaultRouteTabs } from '@/components/_common';
import InvoiceForm from '@/components/Reports/InvoiceForm';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import {
  QuestReportInvoiceFormValues,
  QuestReportInvoiceRequest,
  QuestReportInvoiceTypes,
} from '@/core/domain/questReport/Reports/Invoice/Invoice.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useReportGenerate } from '@/hooks';
import { useQuestReportInvoiceRequest } from '../../../../core/domain/questReport/Reports/Invoice/Invoice.hooks';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { useInvoiceCsv } from '@/core/domain/questReport/CSV/Invoice/Invoice/Invoice.hooks';

const FiscalInvoicePage = ({ route }: PageProps) => {
  const [form] = Form.useForm<QuestReportInvoiceFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useQuestReportInvoiceRequest();
  const generateReportCsv = useInvoiceCsv();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const values = await form.validateFields();

      const obj: QuestReportInvoiceRequest = {
        companiesIds: values.companiesIds?.toString(),
        insurerIds: values.insurerIds?.toString(),
        customerIds: values.customerIds?.toString(),
        suppliersIds: values.suppliersIds?.toString(),
        parts: values.types.includes(QuestReportInvoiceTypes.partsInvoice),
        services: values.types.includes(QuestReportInvoiceTypes.serviceInvoice),
        consumer: values.types.includes(
          QuestReportInvoiceTypes.consumerInvoice,
        ),
        detailItems: values.detailItems,
        startDate: values.initialDate.format(DateTimeFormat),
        endDate: values.finalDate.format(DateTimeFormat),
        cfopIds: values.cfopIds,
        cstPisCofins: values.cstPisCofins,
        situations: values.situations?.toString(),
      };

      const result =
        format === 'PDF' || format === 'REPORT_VIEW'
          ? await generateReport.mutateAsync(obj)
          : await generateReportCsv.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_nota_fiscal.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_nota_fiscal.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_nota_fiscal.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <InvoiceForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
};

export default FiscalInvoicePage;
